import { css } from '@emotion/css';
import React from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  getHonorificNpcNameInJapanese,
  getHonorificNpcNameInKorean
} from 'src/lib/getHonorificNpcName';
import { getHouseType } from 'src/lib/getHouseType';
import { replaceAllSpaces } from 'src/lib/utils';
import { HomeResident } from 'src/models/resort/HomeResident';
import { HouseType } from 'src/models/resort/HouseType';
import { UserColor } from 'src/models/resort/UserColor';
import { FamilyIcon } from '../../common/FamilyIcon';
import { NpcIcons } from '../../common/NpcIcons';
import { ResortEmphasis } from './ResortEmphasis';
import { ResortRibbon } from './ResortRibbon';

interface ImageHeaderHouseProps {
  freeTheme: string;
  npcs: HomeResident[];
  color: UserColor;
}

export const ImageHeaderHouse = ({ freeTheme, npcs, color }: ImageHeaderHouseProps) => {
  const { t, i18n } = useTranslation('resort');

  const houseType = getHouseType(npcs);
  const npcNames = getNpcNames(t, i18n.language, npcs, houseType);

  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        .npcs {
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          transform: translateY(8px);
          z-index: 1;
        }
        .npc-names {
          font-size: 16px;
          color: ${color.textLight};
          margin-bottom: 4px;
        }
        .npc-images {
          line-height: 0;
        }
      `}
    >
      <div className="npcs" role="img" aria-labelledby="npc-names">
        <div id="npc-names" className="npc-names">
          <ResortEmphasis color={color.textLight}>{npcNames}</ResortEmphasis>
        </div>
        <div className="npc-images">
          {houseType === 'normal' ? (
            <NpcIcons npcs={npcs} iconSize={56} />
          ) : (
            <FamilyIcon family={houseType} iconHeight={56} />
          )}
        </div>
      </div>
      <ResortRibbon color={color}>{replaceAllSpaces(freeTheme)}</ResortRibbon>
    </div>
  );
};

function getNpcNames(
  t: TFunction<'resort'>,
  language: string,
  npcs: HomeResident[],
  houseType: HouseType
) {
  switch (houseType) {
    case 'kappei-family':
      if (language === 'ja-JP') {
        return t('kappei_family') + 'さま';
      } else if (language === 'ko-KR') {
        return t('kappei_family') + '님';
      } else {
        return t('kappei_family');
      }
    case 'able-sisters':
      if (language === 'ja-JP') {
        return t('able_sisters') + 'さま';
      } else if (language === 'ko-KR') {
        return t('able_sisters') + '님';
      } else {
        return t('able_sisters');
      }
    case 'normal':
    default:
      const npcNames = getHonorificNpcName(language, npcs);
      return npcNames.length === 2
        ? t('resident_names', { npc_first: npcNames[0], npc_second: npcNames[1] })
        : npcNames[0];
  }
}

function getHonorificNpcName(language: string, npcs: HomeResident[]) {
  switch (language) {
    case 'ja-JP':
      return npcs.map(npc => getHonorificNpcNameInJapanese(npc));
    case 'ko-KR':
      return npcs.map(npc => getHonorificNpcNameInKorean(npc));
    default:
      return npcs.map(npc => npc.name);
  }
}
